import {
  CartErrorEvent,
  CartLinesUpdateEvent,
  CartViewEvent,
  PageViewEvent,
  ProductSelectEvent,
  createViewEventElement
} from '@theme/standard-events';

if (!customElements.get('s-view-event')) {
  customElements.define('s-view-event', createViewEventElement());
}

const toGid = (type, value) => {
  const id = String(value || '');
  if (!id) return '';
  return id.startsWith('gid://') ? id : `gid://shopify/${type}/${id}`;
};

const productHandle = (element) => {
  try {
    return new URL(element.dataset.productUrl || window.location.href, window.location.origin)
      .pathname.split('/').filter(Boolean).pop() || '';
  } catch (_) {
    return '';
  }
};

const selectedOptions = (element) => [...element.querySelectorAll('[data-option-value-id]:checked')]
  .map((input) => ({
    name: input.name?.replace(/^options\[|\]$/g, '') || '',
    value: input.value || ''
  }))
  .filter((option) => option.name && option.value);

const variantFromElement = (element, options) => {
  const price = element?.querySelector('[data-product-price]');
  const id = price?.dataset.variantId;
  if (!id) return null;
  return {
    id: toGid('ProductVariant', id),
    title: price.dataset.variantTitle || options.map((option) => option.value).join(' / '),
    availableForSale: price.dataset.variantAvailable === 'true',
    price: {
      amount: String(price.dataset.variantPrice || '0'),
      currencyCode: price.dataset.variantCurrency || document.body?.dataset.currency || ''
    },
    selectedOptions: options
  };
};

const pendingProductSelections = new WeakMap();
const pendingCartUpdates = new WeakMap();

document.addEventListener('change', (event) => {
  const option = event.target.closest?.('[data-option-value-id]');
  const element = option?.closest?.('product-details');
  if (!option || !element) return;

  const options = selectedOptions(element);
  if (!options.length) return;

  const previous = pendingProductSelections.get(element);
  if (previous) previous.cancel('A newer product selection superseded this lookup.');

  const deferred = ProductSelectEvent.createPromise();
  element.dispatchEvent(new ProductSelectEvent({
    product: {
      id: toGid('Product', element.dataset.productId),
      title: element.dataset.productTitle || '',
      handle: productHandle(element)
    },
    selectedOptions: options,
    promise: deferred.promise
  }));

  const parent = element.parentNode;
  if (!parent) {
    deferred.reject(new Error('Product section parent is unavailable.'));
    return;
  }

  let settled = false;
  let timeout = 0;
  const settle = (callback) => {
    if (settled) return;
    settled = true;
    observer.disconnect();
    window.clearTimeout(timeout);
    if (pendingProductSelections.get(element)?.deferred === deferred) {
      pendingProductSelections.delete(element);
    }
    callback();
  };
  const finish = (next) => settle(() => deferred.resolve({ variant: variantFromElement(next, options) }));
  const cancel = (message) => settle(() => deferred.reject(new Error(message)));

  const observer = new MutationObserver(() => {
    const next = parent.querySelector?.('product-details');
    if (next && next !== element) finish(next);
  });
  observer.observe(parent, { childList: true });

  timeout = window.setTimeout(() => cancel('Product option lookup did not complete.'), 8000);
  pendingProductSelections.set(element, { deferred, cancel });
}, true);

document.addEventListener('submit', (event) => {
  const form = event.target.closest?.('product-details form[action*="/cart/add"]');
  const element = form?.closest?.('product-details');
  if (!form || !element || !form.checkValidity()) return;

  const file = form.querySelector('input[type="file"]')?.files?.[0];
  if (file) return;

  const data = new FormData(form);
  const merchandiseId = String(data.get('id') || '');
  if (!merchandiseId) return;
  const quantity = Math.max(1, Number(data.get('quantity') || 1));

  const previous = pendingCartUpdates.get(element);
  if (previous) previous.reject(new Error('A newer cart update superseded this request.'));

  const deferred = CartLinesUpdateEvent.createPromise();
  pendingCartUpdates.set(element, deferred);
  element.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId, quantity }],
    promise: deferred.promise
  }));
}, true);

document.addEventListener('DOMContentLoaded', () => {
  document.dispatchEvent(new PageViewEvent({
    page: {
      template: document.body?.dataset.pageType || 'page',
      title: document.title,
      url: window.location.href
    }
  }));
}, { once: true });

window.AngesStandardEvents = window.AngesStandardEvents || {
  async dispatchCartView(target, context = 'dialog') {
    try {
      const result = await window.Shopify?.actions?.getCart?.();
      target.dispatchEvent(new CartViewEvent({ context, cart: result?.cart || null }));
    } catch (_) {
      target.dispatchEvent(new CartViewEvent({ context, cart: null }));
    }
  }
};

document.addEventListener('ads:add_to_cart', async (event) => {
  const element = event.target?.closest?.('product-details') || document.querySelector('product-details');
  const deferred = element ? pendingCartUpdates.get(element) : null;
  if (!element || !deferred) return;

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
    const ajaxCart = await response.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart)
    });
  } catch (error) {
    deferred.reject(error);
    element.dispatchEvent(new CartErrorEvent({
      error: error.message || 'The basket request failed.',
      code: 'SERVICE_UNAVAILABLE'
    }));
  } finally {
    pendingCartUpdates.delete(element);
  }
});

document.addEventListener('ads:add_to_cart_error', (event) => {
  const element = event.target?.closest?.('product-details') || document.querySelector('product-details');
  const deferred = element ? pendingCartUpdates.get(element) : null;
  if (!element || !deferred) return;
  const error = new Error('The cake could not be added to the basket.');
  deferred.reject(error);
  element.dispatchEvent(new CartErrorEvent({
    error: error.message,
    code: 'SERVICE_UNAVAILABLE'
  }));
  pendingCartUpdates.delete(element);
});

const croModuleUrl = new URL('./cro-interaction-events.js', import.meta.url);
croModuleUrl.search = new URL(import.meta.url).search;
import(croModuleUrl.href).catch(() => {});
